import { useState } from "react"
import { useUser } from "@clerk/clerk-react"
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Combobox } from "@/components/ui/combobox"
import { cn } from "@/lib/utils/general"
import { CARD_CATEGORIES } from "@/lib/utils/constants/cards"
import { CardPreview } from "@/components/cards/CardPreview"
import { toast } from "@/hooks/useToast"
import { CardCreationState, CardCreationActions } from "@/hooks/useCardCreation"
import { CardCreationRequest } from "@/types/card"
import { Lock } from "lucide-react"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { ScrollArea } from "@/components/ui/scroll-area"
import { GeneratedCardData } from "@/types/card"
import { CardTypeOption } from '@/lib/utils/constants/cards'

interface CardCreationFormProps {
  state: CardCreationState
  actions: CardCreationActions
}

const UNLOCKED_CATEGORIES = ['fantasy', 'tarot', 'ufo'];

// Add this type to help with field dependencies
interface FieldDependencies {
  [category: string]: {
    [field: string]: {
      dependsOn?: {
        field: string;
        values: string[];
      };
    };
  };
}

// Define field dependencies - only for categories that need it
const FIELD_DEPENDENCIES: FieldDependencies = {
  ufo: {
    mediaType: {
      dependsOn: {
        field: 'type',
        values: ['Media Archive']
      }
    },
    entityType: {
      dependsOn: {
        field: 'type',
        values: ['Entity']
      }
    },
    siteType: {
      dependsOn: {
        field: 'type',
        values: ['Site']
      }
    },
    artifactType: {
      dependsOn: {
        field: 'type',
        values: ['Artifact']
      }
    },
    // These fields show for all types
    era: {},
    sourceQuality: {},
    classification: {}
  }
};

export function CardCreationForm({ state, actions }: CardCreationFormProps): JSX.Element {
  const { user } = useUser()
  const [activeTab, setActiveTab] = useState("categories")
  const [selectedCategory, setSelectedCategory] = useState("")
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  const [language, setLanguage] = useState("en")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedBlueprint] = useState("")

  const languageOptions = [
    { label: "English", value: "en" },
    { label: "Spanish", value: "es" },
    { label: "French", value: "fr" },
    { label: "German", value: "de" },
    { label: "Italian", value: "it" },
    { label: "Portuguese", value: "pt" },
    { label: "Russian", value: "ru" },
    { label: "Japanese", value: "ja" },
    { label: "Korean", value: "ko" },
    { label: "Chinese", value: "zh" },
    { label: "Thai", value: "th" }
  ];

  const getInputStyles = (value: string) => {
    return cn(
      "w-full",
      state.hasAttemptedSubmit && !value && "ring-1 ring-red-400/50 dark:ring-red-500/50"
    )
  }

  const isFieldVisible = (category: string, field: string) => {
    if (!FIELD_DEPENDENCIES[category]) {
      // If category has no dependencies defined, show all fields
      return true;
    }

    const fieldConfig = FIELD_DEPENDENCIES[category][field];
    if (!fieldConfig?.dependsOn) {
      // If field has no dependencies, show it
      return true;
    }

    const { field: dependsOnField, values } = fieldConfig.dependsOn;
    return values.includes(fieldValues[dependsOnField]);
  };

  const getRequiredFields = (category: string) => {
    if (!FIELD_DEPENDENCIES[category]) {
      // For categories without dependencies, return all fields
      return Object.keys(CARD_CATEGORIES[category as keyof typeof CARD_CATEGORIES].fields);
    }

    // For categories with dependencies, only return visible fields
    return Object.keys(CARD_CATEGORIES[category as keyof typeof CARD_CATEGORIES].fields)
      .filter(field => isFieldVisible(category, field));
  };

  const isFormValid = selectedCategory && 
    (selectedCategory ? getRequiredFields(selectedCategory).every(
      field => fieldValues[field]
    ) : true) &&
    language

  const handleCreateCard = async () => {
    if (!isFormValid) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in all required fields.",
      })
      return
    }
  
    try {
      setIsSubmitting(true)
      actions.setIsLoading(true)
      actions.setIsPreviewMode(true)
  
      const category = CARD_CATEGORIES[selectedCategory as keyof typeof CARD_CATEGORIES]
  
      // Get the selected type option
      const selectedTypeOption = (category.fields.type as CardTypeOption[])
        .find(t => t.name === fieldValues.type)
      
      if (!selectedTypeOption) {
        throw new Error('Invalid card type selected')
      }

      const requestData: CardCreationRequest = {
        themeId: category.themeId,
        theme: selectedCategory,
        blueprintId: selectedBlueprint,
        fields: {
          ...fieldValues,
          type: selectedTypeOption.baseType,
          cardType: selectedTypeOption.name,
          baseType: selectedTypeOption.baseType
        },
        language,
        playerName: user?.username || user?.firstName || "Unknown Player",
        userId: user?.id?.toString(),
        skipImage: false
      };
  
      const response = await actions.createCard(requestData)
      console.log('Card creation response:', response);
      
      if (!response?.data) {
        throw new Error('Failed to create card')
      }

      // Log the response data structure
      console.log('Response data structure:', {
        title: response.data.title,
        description: response.data.description,
        imageUrl: response.data.imageUrl,
        metadata: response.data.metadata
      });

      const cardData: GeneratedCardData = {
        id: response.data.id,
        title: response.data.title,
        description: response.data.description,
        imageUrl: response.data.imageUrl,
        theme: selectedCategory,
        themeId: category.themeId,
        fields: fieldValues,
        language,
        playerName: user?.username || user?.firstName || "Unknown Player",
        metadata: response.data.metadata
      };

      console.log('Created cardData:', cardData);
      actions.setCardData(cardData)
  
      toast({
        title: "Success",
        description: "Card created successfully!",
      })
    } catch (error) {
      console.error('Card Creation Error:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create card",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="w-full h-full">
      <div className="w-full max-w-7xl mx-auto px-4">
        <div className="flex flex-col items-center justify-center h-full">
          {state.isPreviewMode ? (
            <CardPreview
              data={{
                theme: selectedCategory,
                themeId: CARD_CATEGORIES[selectedCategory as keyof typeof CARD_CATEGORIES].themeId,
                fields: {
                  ...fieldValues,
                  cardClass: fieldValues.cardType || 'Creature'
                },
                language,
                playerName: user?.username || user?.firstName || "Unknown Player"
              }}
              cardData={state.cardData}
              isLoading={state.isLoading}
              onBack={() => actions.setIsPreviewMode(false)}
            />
          ) : (
            <Card className="w-full max-w-[600px]">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-lg md:text-xl">Card Roulette 🎰</CardTitle>
                <CardDescription className="text-sm">Pick your path to reveal what awaits</CardDescription>
              </CardHeader>
              
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-[calc(100vh-200px)]">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="categories">Categories</TabsTrigger>
                  <TabsTrigger value="details" disabled={!selectedCategory}>
                    Card Details
                  </TabsTrigger>
                </TabsList>

                <ScrollArea className="flex-1 w-full">
                  <CardContent className="p-4">
                    <TabsContent value="categories" className="mt-0">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.entries(CARD_CATEGORIES).map(([key, category]) => {
                          const Icon = category.icon
                          const isLocked = !UNLOCKED_CATEGORIES.includes(key)
                          return (
                            <div
                              key={key}
                              className={cn(
                                "flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground relative cursor-pointer transition-colors",
                                selectedCategory === key && "border-primary",
                                isLocked && "opacity-60 cursor-not-allowed hover:bg-popover"
                              )}
                              onClick={() => {
                                if (!isLocked) {
                                  setSelectedCategory(key)
                                  setActiveTab("details")
                                  setFieldValues({})
                                } else {
                                  toast({
                                    variant: "destructive",
                                    title: "Locked Category",
                                    description: "This card type is currently locked. More categories coming soon!",
                                  })
                                }
                              }}
                            >
                              {isLocked && (
                                <div className="absolute top-2 right-2">
                                  <Lock className="h-4 w-4 text-gray-500" />
                                </div>
                              )}
                              <Icon className="mb-3 h-6 w-6" />
                              <span className="text-sm font-medium text-center">{category.label}</span>
                            </div>
                          )
                        })}
                      </div>
                    </TabsContent>

                    <TabsContent value="details" className="space-y-6">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setActiveTab("categories")}
                      >
                        ← Back to Categories
                      </Button>

                      {selectedCategory && (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="type">Type</Label>
                            <Select
                              value={fieldValues.type}
                              onValueChange={(value) => {
                                setFieldValues(prev => ({ ...prev, type: value }))
                              }}
                            >
                              <SelectTrigger id="type" className={getInputStyles(fieldValues.type)}>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                {(CARD_CATEGORIES[selectedCategory as keyof typeof CARD_CATEGORIES].fields.type as CardTypeOption[])
                                  .map((typeOption) => (
                                    <SelectItem key={typeOption.name} value={typeOption.name}>
                                      {typeOption.name}
                                    </SelectItem>
                                  ))
                                }
                              </SelectContent>
                            </Select>
                          </div>
                          {getRequiredFields(selectedCategory).map((field) => {
                            const options = CARD_CATEGORIES[selectedCategory as keyof typeof CARD_CATEGORIES].fields[field];
                            
                            // Skip the 'type' field as it's handled separately
                            if (field === 'type') return null;

                            return (
                              <div key={field} className="space-y-2">
                                <Label htmlFor={field}>{field.charAt(0).toUpperCase() + field.slice(1)}</Label>
                                <Select
                                  value={fieldValues[field]}
                                  onValueChange={(value) => {
                                    setFieldValues(prev => {
                                      const newValues = { ...prev, [field]: value };
                                      if (FIELD_DEPENDENCIES[selectedCategory]?.[field]) {
                                        Object.entries(FIELD_DEPENDENCIES[selectedCategory]).forEach(([depField, config]) => {
                                          if (config.dependsOn?.field === field && !config.dependsOn.values.includes(value)) {
                                            delete newValues[depField];
                                          }
                                        });
                                      }
                                      return newValues;
                                    });
                                  }}
                                >
                                  <SelectTrigger id={field} className={cn("h-10", getInputStyles(fieldValues[field]))}>
                                    <SelectValue placeholder={`Select ${field}`} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Array.isArray(options) && options.map((option) => {
                                      // Handle both string and CardTypeOption
                                      const value = typeof option === 'string' ? option : option.name;
                                      const label = typeof option === 'string' ? option : option.name;
                                      
                                      return (
                                        <SelectItem key={value} value={value}>
                                          {label}
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                              </div>
                            );
                          })}

                          <div className="space-y-2">
                            <Label className="text-sm">Language</Label>
                            <Combobox
                              options={languageOptions}
                              value={language}
                              onChange={setLanguage}
                              placeholder="Select language..."
                            />
                          </div>
                        </div>
                      )}
                    </TabsContent>
                  </CardContent>
                </ScrollArea>

                {activeTab === "details" && (
                  <CardFooter className="border-t p-4 mb-4">
                    <Button 
                      className="w-full" 
                      disabled={!isFormValid || isSubmitting}
                      onClick={handleCreateCard}
                    >
                      {isSubmitting ? "Creating..." : "Create Card"}
                    </Button>
                  </CardFooter>
                )}
              </Tabs>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
} 